import { useQueryClient, useQuery } from "@tanstack/react-query";
import { notificacionError, notificacion, notificacionTipo } from "@/components/Alerts";
import { BaseModal, ModalCloseCross } from "@/components/modals/BaseModal";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import type { Usuario, Respuesta } from "@/ts/typings/clases";
import { useId, useState } from "react";
import { useDisclosure } from "@/hooks/useModal";
import { QueryError } from "@/components/Loading/Loading";
import { useOnChange } from "@/hooks/useOnChange";

interface ModalEditarUsuarioProps {
	readonly shown: boolean;
	readonly close: () => void;
	readonly usuario: Usuario | null;
}
export function ModalEditarUsuario(props: ModalEditarUsuarioProps) {
	const fallbackShown = useDisclosure();
	const [nfcTag, setNfcTag] = useState("");
	const [copiandoNFC, setCopiandoNFC] = useState(false);
	const formId = useId();

	const onCopyNFC = () => {
		const message = JSON.stringify({
			company: "PIHER",
			email: props.usuario?.email,
			hash: props.usuario?.nfctag,
		});

		if (!("NDEFReader" in window)) {
			if ((navigator.clipboard as unknown) == null) {
				fallbackShown.open();
				return;
			}

			navigator.clipboard.writeText(message).then(() => {
				notificacion("Mensaje copiado al portapapeles.");
			}).catch((err: unknown) => {
				notificacionError("Error al copiar al portapapeles.");
				console.error("Error al copiar al portapapeles: ", err);
				fallbackShown.open();
			});
			return;
		}
		setCopiandoNFC(true);
		void copiarNFC(message).then(() => {
			setCopiandoNFC(false);
		});
	};

	if (props.shown && props.usuario?.nfctag == null && nfcTag == "") {
		setNfcTag(getTag(24));
	} else if (props.usuario?.nfctag != null && nfcTag != props.usuario.nfctag) {
		setNfcTag(props.usuario.nfctag);
	}


	return (
		<BaseModal show={props.shown} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">{props.usuario?.email ?? "Nuevo usuario"}</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body">
				<UsuarioForm usuario={props.usuario} id={formId} nfcTag={nfcTag} close={props.close} key={props.usuario?.id ?? "new"} />
			</div>
			<div className="modal-footer">
				{copiandoNFC ? (
					<button type="button" className="btnPrimary" style={{ float: "left" }} disabled>Copiando, ponga la tarjeta en el lector...</button>
				) : (
					<button type="button" onClick={onCopyNFC} className="btnSecondary" style={{ float: "left" }}>Copiar contenido NFC</button>
				)}
				<button type="submit" className="btnPrimary" form={formId}>Guardar</button>
				<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
			</div>
		</BaseModal>
	);
}

interface UsuarioFormProps {
	readonly usuario: Usuario | null;
	readonly nfcTag: string;
	readonly id: string;
	readonly close: () => void;
}
function UsuarioForm(props: UsuarioFormProps) {
	const queryClient = useQueryClient();

	const [rol, setRol] = useState(props.usuario?.rol ?? "");
	const [showPassword, setShowPassword] = useState(false);


	useOnChange(props.usuario, () => {
		if (props.usuario?.rol) setRol(props.usuario.rol);
	});

	function onSubmit(event: React.SubmitEvent) {
		event.preventDefault();
		const formData = new FormData(event.target);
		const formValues = Object.fromEntries(formData.entries());

		if (formValues.email == "") {
			notificacionTipo("error", "Debe insertar un email.");
			return;
		}
		if (formValues.id == "" && formValues.password == "") {
			notificacionTipo("error", "Debe insertar una contraseña.");
			return;
		}

		void sendAjaxRequest<Respuesta>("POST", "/api/v1/anadirusuario", formValues)
			.then((data) => {
				if (data.tipo != "success") {
					notificacionError(data.message ?? "Error al guardar el usuario.");
					return;
				}

				notificacion(data.message ?? "Usuario guardado con éxito.");
				props.close();
				void queryClient.invalidateQueries({ queryKey: ["getListadoUsuarios"] });
			});
	}

	const usuario = props.usuario ?? {
		id: "new",
		nombre: "",
		apellido: "",
		email: "",
		telefono: "",
		control_presencia_key: null,
		rol: "",
		operario: null,
		nfctag: ""
	};
	return (
		<form className="big-box" onSubmit={onSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }} id={props.id}>
			<div>
				<input className="inputsForm" type="hidden" name="id" readOnly value={usuario.id} />

				<label className="labelsForm">Nombre</label>
				<input className="inputsForm" type="text" name="nombre" defaultValue={usuario.nombre} tabIndex={1} />

				<label className="labelsForm">E-mail</label>
				<input className="inputsForm" type="text" name="email" defaultValue={usuario.email} tabIndex={3} />

				<label className="labelsForm">Telefono</label>
				<input className="inputsForm" type="text" name="telefono" defaultValue={usuario.telefono} tabIndex={5} />

				<label className="labelsForm">Clave Control Presencia</label>
				<input className="inputsForm" type="text" name="control_presencia_key" defaultValue={usuario.control_presencia_key ?? ""} tabIndex={7} />

				<div id="divSelectOperario" style={{ display: rol == "operario" ? "" : "none" }}>
					<label className="labelsForm">Perfil Asignado</label>
					<SelectOperarios name="operario" defaultValue={usuario.operario ?? ""} />
				</div>
			</div>
			<div>
				<label className="labelsForm">Apellido</label>
				<input className="inputsForm" type="text" name="apellido" tabIndex={2} defaultValue={usuario.apellido} />
				<label className="labelsForm">Contraseña</label>
				<div style={{ position: "relative" }}>
					<input
						className="inputsForm"
						type={showPassword ? "text" : "password"}
						name="password"
						autoComplete="new-password"
						tabIndex={4}
						aria-label="Contraseña"
						style={{ paddingRight: "38px" }}
					/>
					<button
						type="button"
						onClick={() => { setShowPassword(!showPassword); }}
						aria-pressed={showPassword}
						aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
						style={{
							position: "absolute",
							right: "8px",
							top: "50%",
							transform: "translateY(-50%)",
							background: "none",
							border: "none",
							cursor: "pointer",
							fontSize: "16px",
							padding: 4,
						}}
					>
						<i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
					</button>
				</div>

				<label className="labelsForm">Rol</label>
				<SelectRoles rol={rol} setRol={setRol} />

				<label style={{ marginBottom: "2%" }} />
			</div>
			<div id="fallbackCopiar" style={{ display: "none" }}>
				<textarea value={props.nfcTag} name="nfctag" readOnly />
			</div>
		</form >
	);
}

function SelectRoles({ rol, setRol }: { readonly rol: string, readonly setRol: Setter<string>; }) {
	const { data: roles, error: errorRoles, isPending: isPendingRoles } = useRoles();
	if (errorRoles) {
		return <QueryError error={errorRoles} fallback="Error al cargar los roles." />;
	}
	return (
		<select className="inputsForm" name="rol" tabIndex={7} value={rol} onChange={(event) => { setRol(event.target.value); }}>
			{isPendingRoles == true && <option disabled>Cargando roles...</option>}
			{roles != null && <>
				<option value="">Elige</option>
				{roles.map(item => (
					<option key={item.id} value={item.id}>
						{item.nombre}
					</option>
				))}
			</>}
		</select>
	);
}

function SelectOperarios({ name, defaultValue }: { readonly name: string; readonly defaultValue: string; }) {
	const { data, error, isPending } = useOperariosEkon();
	if (error) return <QueryError error={error} fallback="Error al cargar los operarios." />;

	return (
		<select className="inputsForm" name={name} tabIndex={6} defaultValue={defaultValue}>
			{isPending == true && <option disabled>Cargando operarios...</option>}
			{data != null && <>
				<option value="">Elige</option>
				{data.map(operario => (
					<option value={operario.idOperario} key={operario.idOperario}>
						{operario.nombreOperario}
					</option>
				))}
			</>}
		</select>
	);
}


function getTag(length: number) {
	let result = "";
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
		counter += 1;
	}
	return result;
}
async function copiarNFC(message: string) {
	/* eslint-disable @typescript-eslint/no-unsafe-assignment */
	/* eslint-disable @typescript-eslint/no-unsafe-call */
	/* eslint-disable @typescript-eslint/no-unsafe-member-access */
	try {
		console.log("Solicitando permiso NFC...");
		//@ts-expect-error -- No esta tipado
		const nfc = new NDEFReader();
		await nfc.scan();

		const encoder = new TextEncoder();
		const messageBuffer: ArrayBuffer = encoder.encode(message).buffer;

		console.log("Escribiendo en la tarjeta NFC...");
		await nfc.write({
			records: [
				{
					recordType: "mime",
					mediaType: "application/json",
					data: messageBuffer,
				}
			],
		});

		notificacion("Información escrita correctamente en la tarjeta NFC.");
	} catch (error) {
		notificacionError("Error al escribir en la tarjeta NFC. Asegúrate de que la tarjeta es compatible.");
		console.error(error);
	}
	/* eslint-enable @typescript-eslint/no-unsafe-assignment */
	/* eslint-enable @typescript-eslint/no-unsafe-call */
	/* eslint-enable @typescript-eslint/no-unsafe-member-access */
}


function useOperariosEkon() {
	return useQuery({
		queryKey: ["getOperariosEkon"],
		queryFn: () => {
			return sendAjaxRequest<Operario[]>("GET", "/api/v1/getOperariosEkon");
		},
	});
}
function useRoles() {
	return useQuery({
		queryKey: ["getRoles"],
		queryFn: () => {
			return sendAjaxRequest<Rol[]>("GET", "/api/v1/getRoles", {});
		},
	});
}
export interface Operario {
	idOperario: number,
	nombreOperario: string;
}
export interface Rol {
	id: number,
	nombre: string;
}